import { useCallback } from 'react';
import { useConfirmRequestedValidationPhone as useConfirmRequestedValidationPhoneRequest } from '../api/requests';
import { ErrorCodesEnum } from '../enums/error-codes.enum';
import { authStore$ } from '../stores/auth.store';
import type { ResultData } from '../types';

/** Maps known Magento error messages to error codes for i18n on the frontend */
const getErrorCodeFromMessage = (message: string): string => {
    switch (message) {
        case 'The request code is not correct':
            return 'CODE_MISMATCH';
        case 'The request is expired':
            return 'CODE_EXPIRED';
        case 'The request secret is not correct':
            return 'SECRET_INVALID';
        case 'Phone number is not requested':
            return 'PHONE_NOT_REQUESTED';
        default:
            return ErrorCodesEnum.INVALID_REQUEST;
    }
};

export const useConfirmRequestedValidationPhone = () => {
    const { confirmRequestedValidationPhone: doConfirmRequestedValidationPhone } =
        useConfirmRequestedValidationPhoneRequest();

    return useCallback(
        async (otp: string): Promise<ResultData> => {
            const variables = {
                phoneNumber: authStore$.initValidatePhoneData.get()?.phoneNumber ?? '',
                otp,
                secret: authStore$.requestValidatePhoneResponse.secret.get() ?? 'NO_SECRET',
            };

            try {
                const response = await doConfirmRequestedValidationPhone({ variables });

                if (response.data?.confirmRequestedValidationPhone?.success) {
                    authStore$.confirmRequestedValidationPhone.set(response.data?.confirmRequestedValidationPhone);
                    authStore$.currentOtp.set(otp);
                    return {
                        message: 'Confirm OTP successfully',
                        errorCode: ErrorCodesEnum.SUCCESS,
                    };
                }

                const serverMessage =
                    response.data?.confirmRequestedValidationPhone?.message ?? 'Failed to confirm OTP';
                return {
                    message: serverMessage,
                    errorCode: getErrorCodeFromMessage(serverMessage),
                };
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Failed to confirm OTP';
                return {
                    message,
                    errorCode: getErrorCodeFromMessage(message),
                };
            }
        },
        [doConfirmRequestedValidationPhone]
    );
};
